// Brazilian Portuguese (pt-BR) translations for the paywall + checkout
// mini-localization project. Mirrors the key structure of the English source in
// strings.ts. Tarot card names always stay in English and never appear here.

import type { StringDict } from "./strings";

export const ptBR: StringDict = {
  billing: {
    heading: "Use o Moonlight quando quiser!",
    home: "Início",
    contactUs: "Fale conosco",
    toggleMonthly: "Mensal",
    toggleAnnual: "Anual",
    monthUnit: "mês",
    billedAnnually: "cobrado anualmente por {price}",
    subscribedThroughApple: "Assinatura pela Apple",
    subscribedThroughGoogle: "Assinatura pelo Google Play",
    youreSubscribed: "Você é assinante",
    renews: "Renova em",
    expires: "Expira em",
    managePlan: "Gerenciar plano",
    tryItFree: "Experimente grátis",
    subscribe: "Assinar",
    legacyProPlan: "você está em um plano profissional antigo",
    extendTrialPrompt: "Quer estender seu período de teste grátis?",
    sendANote: "Envie uma mensagem",
    featureList: {
      unlimited: "Leituras de tarô e jogos ilimitados",
      keywords: "Palavras-chave das cartas, dicas de leitura personalizadas e dicas de tarô",
      guidebook: "Seções ampliadas do guia",
      journaling: "Diário + ajuda na interpretação",
      spreads: "Mais de 100 tiragens ou crie a sua própria",
      friends: "Leia com amigos",
      decks: "Inclui 6 baralhos",
    },
    featuresHeading:
      "O Moonlight é intuitivo, fácil de usar e feito para a sua vida real.",
    features: {
      meaningsHeader: "Entenda o significado das cartas",
      meaningsText:
        "Palavras-chave instantâneas e dicas personalizadas do guia para qualquer pergunta que a vida trouxer.",
      anywhereHeader: "Leia em qualquer lugar",
      anywhereText:
        "Acesse lindas tiragens de tarô no celular ou na web, sempre que a inspiração chegar.",
      spreadsHeader: "Tiragens personalizadas",
      spreadsText:
        "Escolha entre mais de 100 layouts prontos ou crie o seu próprio para perguntas específicas.",
      deckArtHeader: "Explore a arte dos baralhos",
      deckArtText:
        "Transforme suas leituras com diferentes estilos de baralho feitos por artistas independentes.",
      friendsHeader: "Leia com amigos",
      friendsText:
        "Aprofunde sua prática individual ou convide amigos para sessões colaborativas ao vivo.",
      practiceHeader: "Crie uma prática diária",
      practiceText:
        "Aprenda tarô com quebra-cabeças interativos, tiragens e sugestões de diário.",
    },
    trial: {
      extended: "teste grátis estendido, sem cobrança automática",
      hourLeft: "resta 1 hora de teste grátis",
      hoursLeft: "restam {count} horas de teste grátis",
      dayLeft: "resta 1 dia de teste grátis",
      daysLeft: "restam {count} dias de teste grátis",
    },
  },
  overlay: {
    titleMonthly: "Assinatura Mensal do Moonlight",
    titleAnnual: "Assinatura Anual do Moonlight",
    autoRenews: "Renova automaticamente até ser cancelada.",
    perMonth: "mês",
    perYear: "ano",
    loadError: "Não foi possível carregar os preços. Verifique sua conexão e tente novamente.",
    retry: "Tentar novamente",
  },
  checkout: {
    processing: "Processando",
    confirm: "Confirmar",
    payNow: "Pagar agora",
    sendRequest: "Enviar solicitação",
    offerCode: "código de oferta",
    checking: "verificando...",
    applied: "aplicado ✓",
    apply: "aplicar",
    remove: "remover",
    enterOfferCode: "Digite um código de oferta",
    invalidCode: "Código inválido",
    errorValidating: "Erro ao validar o código. Tente novamente.",
    percentOff: "{percent}% de desconto",
    amountOff: "{amount} de desconto",
    codeApplied: "Código aplicado",
    oneTimeUse: " (uso único)",
    recurringDiscount: " (desconto recorrente)",
    validForMonth: " (válido por {count} mês)",
    validForMonths: " (válido por {count} meses)",
  },
  cta: {
    leftInTrial: "resta {time} de teste grátis",
    keepGuidebook: "Mantenha o guia completo com uma",
    subscriptionLink: "assinatura Moonlight",
    unlockGuidebook: "Desbloqueie o guia completo com uma",
    tryGuidebook: "Experimente o guia completo",
    hour: "1 hora",
    hours: "{count} horas",
    day: "1 dia",
    days: "{count} dias",
  },
  disclaimer: {
    securePaymentsStripe: "Pagamentos seguros com tecnologia Stripe.",
    securePayments: "Pagamentos seguros.",
    chargedAfterConfirmed: "Você só é cobrado depois que a sessão é confirmada.",
    flexibleRefunds: "Reembolsos flexíveis.",
    moneyBack: "Receba o dinheiro de volta em caso de cancelamento.",
  },
  faq: {
    q1: "O que está incluído na minha assinatura?",
    a1: "Sua assinatura inclui salas próprias ilimitadas, acesso a todos os layouts de tiragem, 4 baralhos grátis com significados, temas personalizados, notas leves, diário, sessões individuais e em grupo, além de suporte multiplataforma (iOS, Android e web).",
    q2: "Quais baralhos de tarô vou receber?",
    a2: "Você recebe 4+ baralhos grátis, incluindo: Rider Waite Smith, Marseille, Summer of Bibliomancy e Internet Lore Tarot. Todos os outros baralhos estão disponíveis separadamente no nosso marketplace. Assim cada artista é remunerado! Você ainda precisa ser assinante para usar os baralhos dos artistas.",
    q3: "Existe um período de teste grátis?",
    a3: "Sim! Oferecemos um período de teste grátis de 3 dias para quem está começando. Se quiser estender seu teste, é só <a href='mailto:support@moonlight.world'>enviar uma mensagem</a>.",
    q4: "Posso cancelar minha assinatura?",
    a4: "Sim, você pode cancelar quando quiser. Sua assinatura permanecerá ativa até o fim do período de cobrança.",
    q5: "Posso usar o Moonlight no meu celular?",
    a5: "Com certeza! O Moonlight funciona tanto em dispositivos móveis quanto em computadores, dando a você flexibilidade para fazer leituras em qualquer lugar. Temos um app para iOS e Android.",
    q6: "Meus convidados precisam assinar para entrar na minha sala?",
    a6: "Não! Qualquer pessoa que entrar na sua sala como convidada (amigos, clientes, etc.) pode participar de graça! Elas só precisam assinar se quiserem fazer as próprias leituras.",
    q7: "Preciso assinar para agendar uma leitura profissional?",
    a7: "Não! Se você estiver agendando com profissionais no Moonlight, essas sessões são pagas separadamente. A assinatura é apenas para usar nossas salas próprias.",
    q8: "A assinatura inclui software de agendamento profissional?",
    a8: "Se você é profissional e quer usar o Moonlight com clientes, será necessário solicitar uma <a href='https://forms.moonlight.world/professional-application'>conta profissional</a>.",
  },
};
